
import { defineComponent, ref, onMounted, reactive } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import starApi from '@/api/starApi'

interface MsgItem {
  id: string
  createTime: number
  messageTitle: string
  messageContent: string
  ext: {
    strategyType: number
    taskId: string
  }
}

function formatTime(ts: number): string {
  if (!ts) return '--'
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const PAGE_SIZE = 20
const MSG_LIST_TYPES = 'strategy'

export default defineComponent({
  name: 'StarMessage',
  emits: ['navigate'],
  setup(props, { emit }) {
    const route = useRoute()
    const store = useStore()

    const list = ref<MsgItem[]>([])
    const loading = ref(false)
    const loadingMore = ref(false)
    const noMore = ref(false)
    const pageNo = ref(1)

    const fetchList = async (page: number) => {
      const token = route.query.token as string
      const userName = store.state.userInfo?.userName

      const isFirst = page === 1
      if (isFirst) loading.value = true
      else loadingMore.value = true

      try {
        const res = await starApi.getMessageList({
          data: {
            msgListType: 'strategy',
            pageNo: page,
            pageSize: PAGE_SIZE
          },
          headers: { token, username: userName }
        })
        const items: MsgItem[] = res?.data?.data || []
        if (isFirst) list.value = items
        else list.value.push(...items)

        const total = res?.data?.total ?? 0
        noMore.value = list.value.length >= total
        pageNo.value = page
      } catch (e) {
        /* 静默失败 */
      } finally {
        loading.value = false
        loadingMore.value = false
      }
    }

    const onScroll = (e: Event) => {
      if (loadingMore.value || noMore.value) return
      const el = e.target as HTMLElement
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
        fetchList(pageNo.value + 1)
      }
    }

    const goDetail = (item: MsgItem) => {
      const ext = item.ext || {}
      const isStock = !ext.taskId?.includes('_com_save')
      const path = isStock ? '/star/stock/result' : '/star/group/result'
      emit('navigate')
      const q = new URLSearchParams()
      const cur = route.query
      if (cur.token) q.set('token', cur.token as string)
      if (cur.softNameAbbr) q.set('softNameAbbr', cur.softNameAbbr as string)
      if (cur.reffer) q.set('reffer', cur.reffer as string)
      if (cur.theme) q.set('theme', cur.theme as string)
      // 兼容三种拼写，透传 userName
      const userName = (cur.userName || cur.username || cur.usename) as string
      if (userName) q.set('userName', userName)
      if (cur.userType) q.set('userType', cur.userType as string)
      q.set('taskID', ext.taskId)
      q.set('isDetails', 'true')
      q.set('isfrom', '2')
      location.href = `${location.pathname}#${path}?${q.toString()}`
    }

    const tooltip = reactive({ visible: false, x: 0, y: 0, text: '' })
    const showTooltip = (e: MouseEvent, text: string) => {
      tooltip.text = text
      tooltip.visible = true
      moveTooltip(e)
    }
    const moveTooltip = (e: MouseEvent) => {
      tooltip.x = Math.max(8, e.clientX - 220)
      tooltip.y = e.clientY + 16
    }
    const hideTooltip = () => { tooltip.visible = false }

    onMounted(() => fetchList(1))

    return { list, loading, loadingMore, noMore, formatTime, onScroll, goDetail, tooltip, showTooltip, moveTooltip, hideTooltip }
  }
})
